/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { SliceCaseReducers, shallowEqual } from '@apia/store';
import { useLatest } from '@apia/util';
import { useEffect, useMemo } from 'react';
import { TLoadedPanelProps, TPanelName, TPanelType } from '../types';
import { panelsActions, panelsStore, registerReducers } from './panelsStore';
import { usePanelSelector } from './usePanelSelector';
import { usePanelIdentity } from '../common';
import { useDashboardContext } from '../dashboard';

declare type ActionCreatorForCaseReducer<CR> = CR extends (
  state: any,
  action: infer Action,
) => any
  ? Action extends {
      payload: infer P;
    }
    ? (payload: P, panelName?: string) => unknown
    : () => unknown
  : () => unknown;

type CaseReducerActions<CaseReducers extends SliceCaseReducers<any>> = {
  [Type in keyof CaseReducers]: ActionCreatorForCaseReducer<CaseReducers[Type]>;
};

export function createPanelStore<
  PanelState = any,
  Reducers extends SliceCaseReducers<PanelState> = SliceCaseReducers<PanelState>,
>(
  panelType: TPanelType,
  initialState: PanelState,
  reducers: Reducers,
): [
  () => CaseReducerActions<Reducers>,
  <Selected>(
    panelName: TPanelName,
    selector: (state: PanelState) => Selected,
    comparator?: (a: Selected, b: Selected) => boolean,
  ) => Selected,
] {
  const useSelector = <Selected>(
    panelName: TPanelName,
    selector: (state: PanelState) => Selected,
    comparator: (a: Selected, b: Selected) => boolean = shallowEqual,
  ): Selected => {
    const dashboard = useDashboardContext();
    const props = useMemo(
      () => dashboard.getPanelByName(panelName)?.props as TLoadedPanelProps,
      [dashboard, panelName],
    );

    const propsRef = useLatest(props);

    useEffect(() => {
      panelsStore.dispatch(
        panelsActions.initState({
          panelType,
          panelId: propsRef.current.id,
          initialState,
        }),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return usePanelSelector<PanelState, Selected>(
      panelType,
      panelName,
      initialState,
      selector,
      comparator,
    );
  };

  registerReducers<PanelState>(panelType, reducers);

  /**
   * Devuelve un objeto de dispatchers asociados al panel. Este hook puede ser
   * exportado pero es importante comprender su funcionamiento: cuando es usado
   * acepta un segundo parámetro opcional panelName que en caso de no ser
   * pasado, utilizará el del contexto del panel desde el que se ejecuta.
   */
  const usePanelDispatcher = () => {
    const { panelName: contextPanelName } = usePanelIdentity();
    const dispatchers: CaseReducerActions<Reducers> =
      {} as CaseReducerActions<Reducers>;
    const dashboard = useDashboardContext();

    Object.entries(reducers).forEach(([name]) => {
      dispatchers[name as keyof typeof dispatchers] = ((
        payload: any,
        panelName?: TPanelName,
      ) => {
        const props = dashboard.getPanelByName(panelName ?? contextPanelName)
          ?.props as TLoadedPanelProps;

        panelsStore.dispatch(
          panelsActions.updatePanelStore({
            panelId: props.id,
            panelType,
            data: { payload },
            reducer: name,
          }),
        );
      }) as any;
    });

    return dispatchers;
  };

  return [usePanelDispatcher, useSelector];
}
